import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DashboardListPageLayout from 'src/common/components/dashboard/DashboardListPageLayout';
import Empty from 'src/common/components/Empty';
import TableHead from 'src/common/components/table/TableHead';
import { frostedPanelBg } from 'src/common/styles/frostedPanelStyles';
import { LanguageKeys } from 'src/common/constants/languages';
import { minutesToHours } from 'src/helpers/dateTimeFormatters';
import { IMyResult } from 'src/interfaces/MyResult';
import { THeadCell } from 'src/interfaces/Table';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import MyResultsEditionFilter from './components/MyResultsEditionFilter';

interface Props {
  results: IMyResult[];
}

export default function MyResultsPage({ results }: Props) {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const navigate = useNavigate();
  const currentLang = resolvedLanguage as LanguageKeys;

  const { activeEditionId, competitionsById, distancesById, editionsById } =
    useSystemOptionsStore(s => s);

  const [editionFilter, setEditionFilter] = useState<string | null>(activeEditionId ?? null);

  const filteredResults = useMemo(() => {
    if (!editionFilter) {
      return results;
    }
    return results.filter(r => r.editionId === editionFilter);
  }, [results, editionFilter]);

  const headCells: readonly THeadCell<IMyResult>[] = [
    { id: 'editionYear', label: t('myResults.year'), width: '10%' },
    { id: 'competitionId', label: t('drawer.competitions'), width: '25%' },
    { id: 'participantId', label: t('myResults.participant'), width: '25%' },
    { id: 'duration', label: t('myResults.time'), width: '15%' },
    { id: 'ser', label: '#', width: '10%' },
  ];

  const grouped = useMemo(() => {
    const byYear = new Map<number, IMyResult[]>();
    filteredResults.forEach(r => {
      const list = byYear.get(r.editionYear) ?? [];
      list.push(r);
      byYear.set(r.editionYear, list);
    });
    return [...byYear.entries()].sort(([a], [b]) => b - a);
  }, [filteredResults]);

  const editionYearLabel =
    editionFilter && editionsById?.[editionFilter]
      ? String(editionsById[editionFilter].year)
      : null;

  const isViewingActiveEdition = !editionFilter || editionFilter === activeEditionId;
  const showCurrentEditionEmpty = filteredResults.length === 0 && isViewingActiveEdition;
  const showPastEditionEmpty =
    filteredResults.length === 0 && !!editionFilter && editionFilter !== activeEditionId;

  return (
    <DashboardListPageLayout
      title={t('myResults.pageTitle')}
      fill={false}
      toolbar={
        <MyResultsEditionFilter
          editionFilter={editionFilter}
          onEditionChange={setEditionFilter}
        />
      }
    >
        {showCurrentEditionEmpty ? (
          <Empty
            icon={<TimerOutlinedIcon fontSize="large" />}
            title={t('myResults.emptyTitle')}
            description={t('myResults.empty')}
            primaryAction={{
              label: t('drawer.myParticipants'),
              onClick: () => navigate('/dashboard/my/participants'),
            }}
          />
        ) : null}

        {showPastEditionEmpty ? (
          <Empty
            icon={<TimerOutlinedIcon fontSize="large" />}
            title={t('myResults.emptyPastEditionTitle', { year: editionYearLabel ?? '' })}
            description={t('myResults.emptyPastEditionDescription')}
          />
        ) : null}

        {!showCurrentEditionEmpty && !showPastEditionEmpty
          ? grouped.map(([year, rows]) => (
              <Box key={year} sx={{ mb: 3 }}>
                {!editionFilter ? (
                  <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                    {year}
                  </Typography>
                ) : null}
                <TableContainer
                  component={Paper}
                  sx={{ backgroundColor: theme => frostedPanelBg(theme, 0.85) }}
                >
                  <Table size="medium">
                    <TableHead<IMyResult> headCells={headCells} />
                    <TableBody>
                      {rows.map(row => {
                        const competition = competitionsById?.[row.competitionId];
                        const distance = distancesById?.[row.distanceId];
                        const competitionLabel = competition?.i18n[currentLang] ?? '-';
                        const distanceLabel = distance?.i18n[currentLang] ?? '-';
                        return (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.editionYear}</TableCell>
                            <TableCell>
                              {t('myResults.competitionDistance', {
                                competition: competitionLabel,
                                distance: distanceLabel,
                              })}
                            </TableCell>
                            <TableCell>
                              {row.lastName} {row.firstName}
                            </TableCell>
                            <TableCell>
                              {row.duration ? minutesToHours(row.duration, t) : '-'}
                            </TableCell>
                            <TableCell>{row.ser ?? '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))
          : null}
    </DashboardListPageLayout>
  );
}
